import React, { useState } from "react";
import { Search, ExternalLink, Plus, Check } from "lucide-react";
import { argus, aErr, Btn, Label, inputCls } from "@/components/argus/ui";
import { DBS } from "@/components/argus/ArgusLookups";

const GROUPS = [
  ["news", "Recent news coverage"],
  ["sec", "US company filings (SEC EDGAR)"],
  ["nonprofits", "US nonprofit tax records (IRS 990s)"],
  ["offshore", "Offshore Leaks (Panama, Pandora, Paradise Papers)"],
];

function Row({ it, cases, i, gk }) {
  const [caseId, setCaseId] = useState(""); const [done, setDone] = useState(false); const [fail, setFail] = useState(false);
  const save = async (id) => {
    setCaseId(id); setFail(false); if (!id) return;
    try { await argus.post(`/cases/${id}/sources/link`, { url: it.url, title: (it.title || "").slice(0, 200) }); setDone(true); }
    catch { setFail(true); setCaseId(""); }
  };
  return (
    <li className="flex items-start gap-2 py-1.5 border-b border-white/5 last:border-0" data-testid={`argus-search-item-${gk}-${i}`}>
      <div className="flex-1 min-w-0">
        <a href={it.url} target="_blank" rel="noopener noreferrer" className="text-sm text-white/90 hover:text-[#34d399] inline-flex items-start gap-1.5">{it.title} <ExternalLink className="w-3 h-3 mt-1 shrink-0 opacity-50" /></a>
        <div className="text-[11px] text-white/40 font-mono">{[it.source, it.date, it.detail].filter(Boolean).join(" · ")}</div>
      </div>
      {cases.length > 0 && (done ? <Check className="w-4 h-4 text-[#34d399] mt-1" data-testid={`argus-search-saved-${gk}-${i}`} /> : (
        <label className={`relative shrink-0 text-[11px] ${fail ? "text-red-300" : "text-white/50"} hover:text-[#34d399] cursor-pointer inline-flex items-center gap-1 mt-0.5`}><Plus className="w-3 h-3" />{fail ? "Retry" : "Save"}
          <select className="absolute inset-0 opacity-0 cursor-pointer" value={caseId} onChange={e => save(e.target.value)} data-testid={`argus-search-save-${gk}-${i}`}>
            <option value="">Save to case…</option>{cases.map(c => <option key={c.case_id} value={c.case_id}>{c.title}</option>)}
          </select>
        </label>
      ))}
    </li>
  );
}

function Profile({ w }) {
  const p = w?.profile;
  if (!p) return null;
  return (
    <div className="p-5 rounded-lg argus-glass flex flex-col sm:flex-row gap-5" data-testid="argus-search-profile">
      {p.image && <img src={p.image} alt={p.title} className="w-28 h-28 object-cover rounded-md border border-white/10 shrink-0" />}
      <div className="min-w-0 flex-1">
        <Label>Profile · Wikipedia & Wikidata</Label>
        <a href={p.url} target="_blank" rel="noopener noreferrer" className="argus-display text-2xl font-bold mt-1 block hover:text-[#34d399]" data-testid="argus-search-profile-title">{p.title}</a>
        {p.description && <div className="text-xs text-[#a7f3d0]/70">{p.description}</div>}
        <p className="text-sm text-white/75 mt-2 leading-relaxed">{p.extract}</p>
        {p.facts?.length > 0 && <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-1 mt-3 text-xs">{p.facts.map(f => <div key={f.label} className="flex gap-2"><dt className="text-[#10b981]/80 font-mono uppercase text-[10px] tracking-wider w-28 shrink-0 pt-0.5">{f.label}</dt><dd className="text-white/80">{f.value}</dd></div>)}</dl>}
        {w.related?.length > 0 && <div className="mt-3 text-[11px] text-white/45">Also: {w.related.map((r, i) => <a key={r.url} href={r.url} target="_blank" rel="noopener noreferrer" className="hover:text-[#34d399]">{r.title}{i < w.related.length - 1 ? " · " : ""}</a>)}</div>}
      </div>
    </div>
  );
}

export default function ArgusSearch({ cases, endpoint = "/search" }) {
  const [q, setQ] = useState(""); const [res, setRes] = useState(null); const [busy, setBusy] = useState(false); const [err, setErr] = useState("");
  const run = async () => {
    if (q.trim().length < 2) return;
    setBusy(true); setErr("");
    try { const { data } = await argus.get(`${endpoint}?q=${encodeURIComponent(q.trim())}`, { timeout: 40000 }); setRes(data); }
    catch (e) { setErr(aErr(e, "Search failed")); } finally { setBusy(false); }
  };
  const r = res?.results || {};
  return (
    <div className="p-5 md:p-8 space-y-5 max-w-5xl" data-testid="argus-search">
      <div>
        <h2 className="argus-display text-3xl font-bold argus-glow-text">Records search</h2>
        <p className="text-sm text-white/55 mt-1">Type a company, organisation or person. Argus checks public databases at once. Free, no AI actions used.</p>
      </div>
      <div className="flex gap-2">
        <div className="relative flex-1"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input className={`${inputCls} !pl-9 !py-3`} value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === "Enter" && run()} placeholder="e.g. Theranos, Red Cross, Elizabeth Holmes" data-testid="argus-search-input" /></div>
        <Btn busy={busy} onClick={run} disabled={q.trim().length < 2} className="!px-5" data-testid="argus-search-btn">Search</Btn>
      </div>
      {err && <div className="text-red-300 text-sm" data-testid="argus-search-err">{err}</div>}
      {res && (
        <div className="space-y-4" data-testid="argus-search-results">
          <Profile w={r.wikipedia?.data} />
          {!r.wikipedia?.data?.profile && <div className="text-xs text-white/40">No encyclopedia profile found for "{res.q}".</div>}
          <div className="grid lg:grid-cols-2 gap-4">
            {GROUPS.map(([k, title]) => {
              const items = r[k]?.data || [];
              return (
                <div key={k} className="p-4 rounded-lg argus-glass" data-testid={`argus-search-group-${k}`}>
                  <div className="flex justify-between"><Label>{title}</Label><span className="text-[10px] font-mono text-white/40">{r[k]?.ok === false ? "unavailable" : items.length}</span></div>
                  {items.length ? <ul className="mt-2">{items.map((it, i) => <Row key={it.url + i} it={it} i={i} gk={k} cases={cases} />)}</ul>
                    : <div className="text-xs text-white/35 mt-2">{r[k]?.ok === false ? "This database didn't answer. Try again shortly." : "Nothing found."}</div>}
                </div>
              );
            })}
          </div>
          <div className="p-4 rounded-lg argus-glass" data-testid="argus-search-more">
            <Label>Check more databases · opens their own search</Label>
            <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-2">
              {DBS.flatMap(g => g.items).map(([label, fn]) => <a key={label} href={fn(res.q)} target="_blank" rel="noopener noreferrer" className="text-xs text-white/70 hover:text-[#34d399] inline-flex items-center gap-1">{label} <ExternalLink className="w-3 h-3 opacity-50" /></a>)}
            </div>
          </div>
          <p className="text-[11px] text-white/40">Public records can be wrong, outdated or about a different person with the same name. Verify before publishing.</p>
        </div>
      )}
    </div>
  );
}
